import { getInputDevicePermissionState } from '@pexip/media-control';
import { createUserMediaStatus, DeniedDevices, hasInputStatus, InputStatusMask, MediaInputStatusFlag, } from './types';
const toPermissionFlag = (state) => {
    switch (state) {
        case 'granted':
            return MediaInputStatusFlag.PermissionGranted;
        case 'denied':
            return MediaInputStatusFlag.PermissionDenied;
        case 'prompt':
            return MediaInputStatusFlag.PermissionPrompt;
    }
};
const hasFlag = (status, kind, flag) => Boolean(status &&
    typeof status === 'object' &&
    hasInputStatus(status, kind, flag));
export const isFallbackVideo = status => hasFlag(status, 'video', MediaInputStatusFlag.Fallback);
export const isFallbackAudio = status => hasFlag(status, 'audio', MediaInputStatusFlag.Fallback);
export const isFallback = status => isFallbackAudio(status) || isFallbackVideo(status);
export const hasNoDevice = status => hasFlag(status, 'audio', MediaInputStatusFlag.NoDevice) &&
    hasFlag(status, 'video', MediaInputStatusFlag.NoDevice);
export const hasNoAudioDevices = status => hasFlag(status, 'audio', MediaInputStatusFlag.NoDevice);
export const hasNoVideoDevices = status => hasFlag(status, 'video', MediaInputStatusFlag.NoDevice);
export const areBothGranted = status => hasFlag(status, 'audio', MediaInputStatusFlag.TrackPresent) &&
    hasFlag(status, 'video', MediaInputStatusFlag.TrackPresent);
export const isGrantedVideo = status => hasFlag(status, 'video', MediaInputStatusFlag.TrackPresent);
export const isGrantedAudio = status => hasFlag(status, 'audio', MediaInputStatusFlag.TrackPresent);
export const isGranted = status => isGrantedVideo(status) || isGrantedAudio(status);
const hasInputError = (status, kind) => (status[kind] & InputStatusMask.Error) !== 0;
export const isOnlyAudioError = status => Boolean(status &&
    typeof status === 'object' &&
    hasInputError(status, 'audio') &&
    !hasInputError(status, 'video'));
export const isOnlyVideoError = status => Boolean(status &&
    typeof status === 'object' &&
    hasInputError(status, 'video') &&
    !hasInputError(status, 'audio'));
export const isRejected = status => hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied) &&
    hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied);
export const isRejectedOnlyAudio = status => hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied);
export const isRejectedOnlyVideo = status => hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied);
export const isOverConstrained = status => hasFlag(status, 'audio', MediaInputStatusFlag.Overconstrained) ||
    hasFlag(status, 'video', MediaInputStatusFlag.Overconstrained);
export const isUnknownError = status => hasFlag(status, 'audio', MediaInputStatusFlag.UnknownError) ||
    hasFlag(status, 'video', MediaInputStatusFlag.UnknownError);
/**
 * Checks whether either requested media input timed out.
 *
 * @example
 * isTimedOut(createUserMediaStatus({audio: MediaInputStatusFlag.TimedOut}));
 */
export const isTimedOut = status => hasFlag(status, 'audio', MediaInputStatusFlag.TimedOut) ||
    hasFlag(status, 'video', MediaInputStatusFlag.TimedOut);
export const isInitial = status => Boolean(status &&
    typeof status === 'object' &&
    status.audio === MediaInputStatusFlag.None &&
    status.video === MediaInputStatusFlag.None);
export const isInitialPermissions = status => Boolean(status &&
    typeof status === 'object' &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.Requested) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.Requested) &&
    ((status.audio & InputStatusMask.Permission) !== 0 ||
        (status.video & InputStatusMask.Permission) !== 0));
export const isInitialPermissionsNotGranted = status => isInitialPermissions(status) &&
    (hasFlag(status, 'audio', MediaInputStatusFlag.PermissionPrompt) ||
        hasFlag(status, 'video', MediaInputStatusFlag.PermissionPrompt)) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.PermissionGranted) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.PermissionGranted);
export const isRequestPermissionNeeded = status => isInitialPermissions(status) &&
    (hasFlag(status, 'audio', MediaInputStatusFlag.PermissionPrompt) ||
        hasFlag(status, 'video', MediaInputStatusFlag.PermissionPrompt)) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.PermissionDenied) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.PermissionDenied);
export const isInitialPermissionsGranted = status => isInitialPermissions(status) &&
    (hasFlag(status, 'audio', MediaInputStatusFlag.PermissionGranted) ||
        hasFlag(status, 'video', MediaInputStatusFlag.PermissionGranted));
export const isAudioDeviceInUse = status => hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse) &&
    !hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse);
export const isDeviceInUse = status => hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse) ||
    hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse);
export const isDevicesInUse = status => hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse) &&
    hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse);
export const isVideoDeviceInUse = status => hasFlag(status, 'video', MediaInputStatusFlag.DeviceInUse) &&
    !hasFlag(status, 'audio', MediaInputStatusFlag.DeviceInUse);
export const toDeniedDevices = (status) => {
    if (!status) {
        return undefined;
    }
    if (isRejectedOnlyVideo(status)) {
        return DeniedDevices.Camera;
    }
    if (isRejectedOnlyAudio(status)) {
        return DeniedDevices.Microphone;
    }
    if (isRejected(status)) {
        return DeniedDevices.Both;
    }
};
export const getPermissionStatus = ({ audio, video, }) => createUserMediaStatus({
    audio: toPermissionFlag(audio),
    video: toPermissionFlag(video),
});
export const deriveInitialPermissionStatus = async (prevStatus, getPermissionState = getInputDevicePermissionState) => {
    if (isInitialPermissions(prevStatus) || isRejected(prevStatus)) {
        return prevStatus;
    }
    const permission = await getPermissionState();
    const deriveFlag = (kind, state) => {
        if (hasFlag(prevStatus, kind, MediaInputStatusFlag.PermissionDenied)) {
            return MediaInputStatusFlag.PermissionDenied;
        }
        if (hasFlag(prevStatus, kind, MediaInputStatusFlag.TrackPresent)) {
            return MediaInputStatusFlag.PermissionGranted;
        }
        return toPermissionFlag(state);
    };
    return createUserMediaStatus({
        audio: deriveFlag('audio', permission.audio),
        video: deriveFlag('video', permission.video),
    });
};
//# sourceMappingURL=status.js.map